/**
 * 사용 설명서.
 *
 * 시스템 문서가 "왜 이렇게 만들었나" 를 개발자에게 말한다면, 이것은 "무엇을 어떻게 누르나" 를
 * **현장의 운영 담당자**에게 말한다. 그래서 문단보다 요소가 주인공이다 — 번호 붙은 단계,
 * 색으로 구분한 주의 상자, 콘솔과 똑같이 생긴 배지, 누르면 그 메뉴로 가는 단추.
 *
 * 두 화면이다. **첫 화면**(`/manual`)은 처음 온 사람에게 순서를 보여주고 찾는 사람에게 검색을
 * 준다. **장**(`/manual/:slug`)은 메뉴 하나를 처음부터 끝까지 설명한다.
 */
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  ChevronDown,
  CircleCheck,
  Compass,
  ExternalLink,
  Images,
  Info,
  Lightbulb,
  LifeBuoy,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { CAPABILITY_LABEL, FEATURE_ACCESS } from '../capabilities.js';
import { Badge } from '../components/ui.js';
import { Figure } from '../manual/figures.js';
import { ScreenGuide, ShotCrop, shotUrl } from '../manual/ScreenGuide.js';
import { ManualDownload } from '../components/ManualDownload.js';
import {
  MANUAL_CHAPTERS,
  MANUAL_GROUPS,
  QUICK_START,
  chapterBySlug,
  screenGuideEntries,
  searchManual,
} from '../manual/index.js';
import type { CalloutTone, ManualBlock, ManualChapter } from '../manual/types.js';
import { NAV_SECTIONS, SYSTEM_ADMIN_NAV } from '../nav.js';

/** 장의 아이콘 — 메뉴에 매인 장은 사이드바와 같은 그림을 써서 "그 메뉴" 임을 알아보게 한다. */
const MENU_ICONS = new Map<string, LucideIcon>(
  [...NAV_SECTIONS.flatMap((section) => section.items), ...SYSTEM_ADMIN_NAV.items].map((entry) => [entry.path, entry.icon]),
);

function chapterIcon(chapter: ManualChapter): LucideIcon {
  return chapter.icon ?? (chapter.menuPath ? MENU_ICONS.get(chapter.menuPath) : undefined) ?? BookOpen;
}

/**
 * 장 머리의 권한 안내.
 *
 * 둘을 구별한다. 메뉴 자체가 안 보이는 경우와, 메뉴는 보이지만 저장하면 서버가 거절하는
 * 경우다. 뒤쪽은 콘솔이 단추를 감추지 않으므로 누르고 나서야 알게 된다 — 그래서 **지금
 * 계정으로 되는지**까지 말한다.
 */
export function accessNote(chapter: ManualChapter, capabilities: readonly string[]): string | null {
  const label = (capability: string): string => CAPABILITY_LABEL[capability] ?? capability;
  // 기능 표식은 권한 하나가 아니라 서버의 모듈과 계정이 함께 정한다 — 누구에게 보이는지를 그대로 말한다.
  const feature = chapter.capability ? FEATURE_ACCESS[chapter.capability] : undefined;
  if (chapter.capability && feature) {
    return capabilities.includes(chapter.capability)
      ? `이 메뉴는 ${feature.who}에만 보입니다`
      : `이 메뉴는 ${feature.who}에만 보여 지금 계정에는 보이지 않습니다. 필요하면 관리자에게 요청하세요`;
  }
  if (chapter.capability) {
    return capabilities.includes(chapter.capability)
      ? `이 메뉴는 ‘${label(chapter.capability)}’ 권한이 있는 계정에만 보입니다`
      : `이 메뉴는 ‘${label(chapter.capability)}’ 권한이 필요해 지금 계정에는 보이지 않습니다. 필요하면 관리자에게 요청하세요`;
  }
  if (chapter.editCapability) {
    const need = `누구나 볼 수 있고, 만들거나 고치려면 ‘${label(chapter.editCapability)}’ 권한이 필요합니다`;
    return capabilities.includes(chapter.editCapability)
      ? `${need}. 지금 계정은 고칠 수 있습니다`
      : `${need}. 지금 계정은 보기만 할 수 있습니다`;
  }
  return null;
}

/* ────────────────────────────── 글 ────────────────────────────── */

const INLINE = /(\*\*[^*]+\*\*)|(\[\[[^\]]+\]\])/g;

/**
 * `**굵게**` 와 `[[단추]]` 만 안다.
 *
 * 단추 이름을 단추처럼 그리는 것이 요점이다. "저장을 누르세요" 보다 테두리 친 `저장` 이
 * 화면에서 찾을 모양을 그대로 알려준다.
 */
export function ManualText({ text }: { text: string }): ReactNode {
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0;
    if (at > last) nodes.push(text.slice(last, at));
    const token = match[0];
    if (token.startsWith('**')) {
      nodes.push(
        <strong key={at} className="font-semibold text-slate-900">
          {token.slice(2, -2)}
        </strong>,
      );
    } else {
      nodes.push(
        <kbd
          key={at}
          className="mx-0.5 inline-block whitespace-nowrap rounded-md border border-slate-300 border-b-2 bg-white px-1.5 py-px font-sans text-[12.5px] font-medium leading-snug text-slate-800"
        >
          {token.slice(2, -2)}
        </kbd>,
      );
    }
    last = at + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return <>{nodes}</>;
}

const CALLOUT: Record<CalloutTone, { icon: typeof Info; box: string; iconClass: string; label: string }> = {
  tip: {
    icon: Lightbulb,
    box: 'border-th-6/70 bg-th-2/60',
    iconClass: 'text-[#4d7c0f]',
    label: '도움말',
  },
  note: {
    icon: Info,
    box: 'border-blue-200 bg-blue-50/70',
    iconClass: 'text-blue-600',
    label: '참고',
  },
  warn: {
    icon: AlertTriangle,
    box: 'border-amber-300 bg-amber-50',
    iconClass: 'text-amber-600',
    label: '주의',
  },
};

function Block({ block }: { block: ManualBlock }): ReactNode {
  switch (block.kind) {
    case 'text':
      return (
        <p className="my-3 text-[14px] leading-[1.8] text-slate-600">
          <ManualText text={block.text} />
        </p>
      );

    case 'steps':
      return (
        <div className="my-5 rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
          <p className="mb-4 text-[13.5px] font-semibold text-slate-900">{block.title}</p>
          <ol className="space-y-0">
            {block.steps.map((step, at) => {
              const lastStep = at === block.steps.length - 1;
              return (
                <li key={at} className="relative flex gap-3.5 pb-5 last:pb-0">
                  {/* 번호를 잇는 세로선 — 단계가 한 흐름이라는 것을 눈으로 보여준다. */}
                  {!lastStep && <span className="absolute left-[13px] top-7 h-[calc(100%-1.5rem)] w-px bg-slate-200" />}
                  <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-th-3 text-[12.5px] font-semibold text-white">
                    {at + 1}
                  </span>
                  <div className="min-w-0 pt-0.5">
                    <p className="text-[14px] font-medium leading-snug text-slate-900">
                      <ManualText text={step.title} />
                    </p>
                    {step.body && (
                      <p className="mt-1 text-[13px] leading-relaxed text-slate-500">
                        <ManualText text={step.body} />
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      );

    case 'callout': {
      const style = CALLOUT[block.tone];
      const Icon = style.icon;
      return (
        <div className={`my-4 flex gap-3 rounded-xl border px-4 py-3.5 ${style.box}`} role="note">
          <Icon className={`mt-0.5 h-[18px] w-[18px] shrink-0 ${style.iconClass}`} strokeWidth={2} />
          <div className="min-w-0">
            <p className="text-[13.5px] font-semibold text-slate-900">
              <span className="sr-only">{style.label}: </span>
              <ManualText text={block.title} />
            </p>
            <p className="mt-1 text-[13px] leading-relaxed text-slate-600">
              <ManualText text={block.text} />
            </p>
          </div>
        </div>
      );
    }

    case 'legend':
      return (
        <div className="my-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <p className="border-b border-slate-100 bg-slate-50/70 px-4 py-2.5 text-[13px] font-semibold text-slate-800">
            {block.title}
          </p>
          <ul className="divide-y divide-slate-100">
            {block.items.map((item) => (
              <li key={item.label} className="flex flex-col gap-1.5 px-4 py-2.5 sm:flex-row sm:items-center sm:gap-4">
                <span className="w-36 shrink-0">
                  <Badge tone={item.tone}>{item.label}</Badge>
                </span>
                <span className="text-[13px] leading-relaxed text-slate-600">
                  <ManualText text={item.meaning} />
                </span>
              </li>
            ))}
          </ul>
        </div>
      );

    case 'actions':
      return (
        <div className="my-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <p className="border-b border-slate-100 bg-slate-50/70 px-4 py-2.5 text-[13px] font-semibold text-slate-800">
            {block.title}
          </p>
          <dl className="divide-y divide-slate-100">
            {block.items.map((item) => (
              <div key={item.label} className="flex flex-col gap-1 px-4 py-2.5 sm:flex-row sm:gap-4">
                <dt className="shrink-0 sm:w-44">
                  <ManualText text={`[[${item.label}]]`} />
                </dt>
                <dd className="text-[13px] leading-relaxed text-slate-600">
                  <ManualText text={item.text} />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      );

    case 'table':
      return (
        <div className="my-5 overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[480px] border-collapse text-[13px]">
            <thead>
              <tr className="bg-slate-50/70">
                {block.header.map((cell) => (
                  <th key={cell} className="border-b border-slate-200 px-4 py-2.5 text-left font-semibold text-slate-700">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowAt) => (
                <tr key={rowAt} className="border-b border-slate-100 last:border-0">
                  {row.map((cell, cellAt) => (
                    <td
                      key={cellAt}
                      className={`px-4 py-2.5 align-top leading-relaxed ${cellAt === 0 ? 'font-medium text-slate-800' : 'text-slate-600'}`}
                    >
                      <ManualText text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case 'figure':
      return (
        <figure className="my-6">
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:p-6">
            <Figure id={block.figure} />
          </div>
          <figcaption className="mt-2 text-center text-[12.5px] text-slate-500">{block.caption}</figcaption>
        </figure>
      );

    case 'faq':
      return (
        <div className="my-5 space-y-2">
          {block.items.map((item) => (
            <details key={item.q} className="group rounded-xl border border-slate-200 bg-white open:border-slate-300">
              <summary className="flex cursor-pointer list-none items-start gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
                <span className="mt-px shrink-0 text-[13.5px] font-bold text-th-4">Q</span>
                <span className="flex-1 text-[13.5px] font-medium leading-snug text-slate-900">{item.q}</span>
                <ChevronDown
                  className="mt-0.5 h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-180"
                  strokeWidth={2}
                />
              </summary>
              <div className="flex gap-3 border-t border-slate-100 px-4 py-3">
                <span className="mt-px shrink-0 text-[13.5px] font-bold text-slate-400">A</span>
                <p className="text-[13px] leading-relaxed text-slate-600">
                  <ManualText text={item.a} />
                </p>
              </div>
            </details>
          ))}
        </div>
      );

    case 'screen':
      return (
        <ScreenGuide
          shot={block.shot}
          title={block.title}
          markers={block.markers}
          renderText={(text) => <ManualText text={text} />}
        />
      );

    case 'scenario':
      return (
        <div className="my-6 overflow-hidden rounded-xl border border-th-6/70 bg-white">
          <div className="flex gap-3 border-b border-th-6/50 bg-th-2/50 px-4 py-3.5 sm:px-5">
            <Compass className="mt-0.5 h-5 w-5 shrink-0 text-[#4d7c0f]" strokeWidth={2} />
            <div className="min-w-0">
              <p className="text-[11.5px] font-semibold tracking-wide text-[#4d7c0f]">이럴 때는 이렇게</p>
              <p className="mt-0.5 text-[15px] font-semibold text-slate-900">{block.title}</p>
              <p className="mt-1 text-[13px] leading-relaxed text-slate-600">
                <ManualText text={block.situation} />
              </p>
            </div>
          </div>
          <ol className="divide-y divide-slate-100">
            {block.steps.map((step, at) => {
              const crop = step.shot && step.marker;
              return (
                <li
                  key={at}
                  className={`grid gap-3 px-4 py-4 sm:px-5 ${crop ? 'md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] md:items-center md:gap-5' : ''}`}
                >
                  <div className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-th-3 text-[12.5px] font-semibold text-white">
                      {at + 1}
                    </span>
                    <p className="pt-0.5 text-[14px] leading-relaxed text-slate-800">
                      <ManualText text={step.text} />
                    </p>
                  </div>
                  {crop ? <ShotCrop shot={step.shot ?? ''} marker={step.marker ?? ''} /> : null}
                </li>
              );
            })}
          </ol>
          <div className="flex gap-2 border-t border-slate-100 bg-slate-50/80 px-4 py-3 sm:px-5">
            <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" strokeWidth={2.2} />
            <p className="text-[13px] leading-relaxed text-slate-700">
              <ManualText text={block.result} />
            </p>
          </div>
        </div>
      );
  }
}

/* ────────────────────────────── 첫 화면 ────────────────────────────── */

export function ManualHomeScreen(): ReactNode {
  const [query, setQuery] = useState('');
  const results = useMemo(() => searchManual(query), [query]);
  const searching = query.trim().length > 0;

  return (
    <div className="space-y-6">
      <section
        className="relative overflow-hidden rounded-xl px-5 py-7 sm:px-8 sm:py-9"
        style={{ background: 'linear-gradient(135deg, rgb(var(--th-3)) 0%, rgb(var(--th-3) / 0.88) 100%)' }}
      >
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full"
          style={{ background: 'radial-gradient(circle, rgb(var(--th-5) / 0.18) 0%, transparent 70%)' }}
        />
        <div className="relative max-w-3xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11.5px] font-medium tracking-wide text-th-6">
            <LifeBuoy className="h-3.5 w-3.5" strokeWidth={2} />
            사용 설명서
          </span>
          <h1 className="mt-4 text-[26px] font-semibold leading-tight tracking-tight text-white sm:text-[30px]">
            SignageOne 콘솔 사용 안내
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-300">
            매장·로비의 화면에 <strong className="font-semibold text-white">무엇을, 언제, 어느 화면에</strong> 재생할지
            정하는 방법을 메뉴 순서대로 설명합니다. 찾는 기능을 검색하거나, 아래 순서대로 따라 하세요.
          </p>

          {/* 검색 — 설명서를 여는 사람의 절반은 "이거 어디서 하지" 를 들고 온다. */}
          <label className="relative mt-5 block max-w-xl">
            <span className="sr-only">설명서 검색</span>
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="예: 영상 올리기, 점심, 정비 모드, 오프라인"
              className="h-9 w-full rounded-lg border border-white/10 bg-white pl-10 pr-10 text-[13.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-th-5/60"
            />
            {searching && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="검색어 지우기"
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </label>

          {/* 콘솔을 못 여는 자리(교육장·인수인계·현장 점검)를 위해 한 벌로 내보낸다. */}
          <ManualDownload />
        </div>
      </section>

      {searching ? (
        <section className="rounded-xl border border-slate-200 bg-white">
          <p className="border-b border-slate-100 px-5 py-3 text-[13px] text-slate-500">
            <strong className="font-semibold text-slate-900">‘{query.trim()}’</strong> 검색 결과 {results.length}건
          </p>
          {results.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-[14px] font-medium text-slate-800">찾는 내용이 없습니다</p>
              <p className="mt-1 text-[13px] text-slate-500">
                더 짧은 낱말로 검색하거나, <strong className="font-medium">문제 해결</strong> 장을 확인하세요.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {results.map((result) => (
                <li key={`${result.chapter.slug}-${result.section.id}`}>
                  <Link
                    to={`/manual/${result.chapter.slug}#${result.section.id}`}
                    className="group flex items-start gap-4 px-5 py-3.5 transition-colors hover:bg-slate-50"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] text-slate-400">{result.chapter.title}</p>
                      <p className="text-[14px] font-medium text-slate-900">{result.section.title}</p>
                      {result.snippet && (
                        <p className="mt-0.5 line-clamp-2 text-[12.5px] leading-relaxed text-slate-500">{result.snippet}</p>
                      )}
                    </div>
                    <ArrowRight className="mt-5 h-4 w-4 shrink-0 text-slate-300 group-hover:text-slate-500" strokeWidth={2} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <>
          {/* 처음 한 번의 순서. 메뉴는 작업 순서가 아니라 성격대로 놓여 있어서, 순서는 따로 보여줘야 한다. */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
            <h2 className="text-[16px] font-semibold text-slate-900">처음 한 번, 이 순서로</h2>
            <p className="mt-1 text-[13px] text-slate-500">
              화면 한 대에 콘텐츠가 나오기까지의 다섯 단계입니다. 단계를 누르면 해당 설명으로 이동합니다.
            </p>
            <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {QUICK_START.map((step, at) => (
                <li key={step.title}>
                  <Link
                    to={step.to}
                    className="group flex h-full flex-col rounded-lg border border-slate-200 p-4 transition-colors hover:border-th-4 hover:bg-th-2/30"
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-th-3 text-[12.5px] font-semibold text-white">
                      {at + 1}
                    </span>
                    <p className="mt-3 text-[14px] font-semibold text-slate-900">{step.title}</p>
                    <p className="mt-1 flex-1 text-[12.5px] leading-relaxed text-slate-500">{step.body}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-slate-400 group-hover:text-slate-700">
                      {step.menu}
                      <ArrowRight className="h-3 w-3" strokeWidth={2.2} />
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </section>

          <ScreenGuideGallery />

          {MANUAL_GROUPS.map((group) => (
            <section key={group.title}>
              <h2 className="mb-3 px-1 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-400">{group.title}</h2>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {group.chapters.map((chapter) => (
                  <ChapterCard key={chapter.slug} chapter={chapter} />
                ))}
              </div>
            </section>
          ))}

          {/* 읽는 법 — 단추 모양과 상자 색이 무슨 뜻인지 한 번만 알려두면 나머지는 저절로 읽힌다. */}
          <section className="rounded-xl border border-dashed border-slate-300 bg-white/60 p-5">
            <h2 className="text-[14px] font-semibold text-slate-900">이 설명서를 읽는 법</h2>
            <ul className="mt-3 grid gap-3 text-[13px] text-slate-600 md:grid-cols-2">
              <li className="flex items-center gap-2">
                <ManualText text="[[저장]]" />
                <span>테두리 친 글자는 화면에 보이는 단추·메뉴 이름 그대로입니다.</span>
              </li>
              <li className="flex items-center gap-2">
                <Badge tone="ok">온라인</Badge>
                <span>둥근 표시는 콘솔의 상태 표시와 같은 모양입니다.</span>
              </li>
              {(Object.keys(CALLOUT) as CalloutTone[]).map((tone) => {
                const style = CALLOUT[tone];
                const Icon = style.icon;
                return (
                  <li key={tone} className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[12px] font-medium ${style.box}`}>
                      <Icon className={`h-3.5 w-3.5 ${style.iconClass}`} strokeWidth={2} />
                      {style.label}
                    </span>
                    <span>
                      {tone === 'tip' && '더 효율적으로 쓰는 방법'}
                      {tone === 'note' && '미리 알아 두면 헷갈리지 않는 사실'}
                      {tone === 'warn' && '되돌리기 어렵거나 실수가 잦은 부분 — 반드시 확인하세요'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}

/**
 * 화면으로 보는 가이드 — 첫 화면에서 곧장 실제 화면 그림으로 들어가는 입구.
 *
 * 글 목록만 있으면 "그림으로 된 설명이 있다" 는 것을 장을 열어 봐야 안다. 썸네일이 곧
 * 안내다 — 자기가 쓰는 화면을 알아보고 누르면 그 화면의 번호 설명으로 바로 간다.
 */
function ScreenGuideGallery(): ReactNode {
  const entries = screenGuideEntries();
  const shots = new Set(
    MANUAL_CHAPTERS.flatMap((chapter) =>
      chapter.sections.flatMap((section) => section.blocks.flatMap((block) => (block.kind === 'screen' ? [block.shot] : []))),
    ),
  ).size;
  const stories = entries.reduce((sum, entry) => sum + entry.scenarios, 0);
  if (entries.length === 0) return null;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-[16px] font-semibold text-slate-900">
            <Images className="h-[18px] w-[18px] text-th-4" strokeWidth={2} />
            화면으로 보는 가이드
          </h2>
          <p className="mt-1 max-w-3xl text-[13px] leading-relaxed text-slate-500">
            실제 화면에 번호를 붙여 무엇이 어디에 있는지 보여주고, 자주 하는 작업을 그림과 함께 단계별로 안내합니다.
            보려는 화면을 누르면 해당 화면의 둘러보기로 이동합니다.
          </p>
        </div>
        <span className="text-[12px] text-slate-400">
          화면 {shots}장 · 따라 하기 {stories}개
        </span>
      </div>

      <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {entries.map((entry) => {
          const url = shotUrl(entry.shot);
          return (
            <li key={entry.chapter.slug}>
              <Link
                to={`/manual/${entry.chapter.slug}#tour`}
                className="group flex h-full flex-col overflow-hidden rounded-lg border border-slate-200 transition-colors hover:border-th-4"
              >
                <span className="relative block aspect-[16/10] overflow-hidden border-b border-slate-100 bg-slate-100">
                  {url ? (
                    <img
                      src={url}
                      alt=""
                      loading="lazy"
                      draggable={false}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.8]"
                      // 화면 전체를 줄이면 글자가 점이 된다. 본문 쪽을 당겨 보여줘야 무슨 화면인지 알아본다 —
                      // 콘솔은 왼쪽이 사이드바라 제목·도구 막대가 있는 오른쪽 위를, 로그인·기기 화면은 가운데가 내용이라 가운데를.
                      style={{
                        transform: 'scale(1.7)',
                        transformOrigin: /^(login|player-)/.test(entry.shot) ? '50% 45%' : '66% 0%',
                      }}
                    />
                  ) : null}
                  <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-th-3/90 px-2 py-0.5 text-[11px] font-semibold text-white">
                    번호 설명 {entry.markers}개
                  </span>
                </span>
                <span className="flex flex-1 items-center gap-2 px-3 py-2.5">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-semibold text-slate-900">{entry.chapter.title}</span>
                    <span className="block text-[12px] text-slate-500">
                      화면 {entry.screens}장{entry.scenarios > 0 ? ` · 따라 하기 ${entry.scenarios}개` : ''}
                    </span>
                  </span>
                  <ArrowRight
                    className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-600"
                    strokeWidth={2}
                  />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ChapterCard({ chapter }: { chapter: ManualChapter }): ReactNode {
  const Icon = chapterIcon(chapter);
  return (
    <Link
      to={`/manual/${chapter.slug}`}
      className="group flex gap-3.5 rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300 hover:bg-slate-50/60"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-th-3">
        <Icon className="h-[18px] w-[18px] text-th-6" strokeWidth={1.9} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 text-[14px] font-semibold text-slate-900">
          {chapter.title}
          {chapter.capability && (
            <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-slate-400">
              <ShieldCheck className="h-3 w-3" strokeWidth={2} />
              {CAPABILITY_LABEL[chapter.capability] ?? FEATURE_ACCESS[chapter.capability]?.label ?? chapter.capability}
            </span>
          )}
        </span>
        <span className="mt-1 block text-[12.5px] leading-relaxed text-slate-500">{chapter.summary}</span>
      </span>
    </Link>
  );
}

/* ────────────────────────────── 장 ────────────────────────────── */

export function ManualChapterScreen({ capabilities }: { capabilities: readonly string[] }): ReactNode {
  const { slug = '' } = useParams();
  const location = useLocation();
  const chapter = chapterBySlug(slug);

  /*
   * 앵커로 들어왔으면 그 절로 옮긴다. 없으면 맨 위로 — 긴 장을 읽다 다음 장으로 넘어갔는데
   * 스크롤이 중간에 멈춰 있으면 첫 절을 건너뛰게 된다.
   *
   * **`main` 만 굴린다.** 본문은 창이 아니라 `main` 이 굴리는데, `scrollIntoView` 는 조상을
   * 전부 굴려서 `overflow: hidden` 인 셸까지 밀려 올라가 사이드바째 위가 비어 버렸다.
   */
  useEffect(() => {
    const main = document.querySelector('main');
    if (!main) return;
    const anchor = decodeURIComponent(location.hash.replace(/^#/, ''));
    const target = anchor ? document.getElementById(anchor) : null;
    const top = target
      ? target.getBoundingClientRect().top - main.getBoundingClientRect().top + main.scrollTop - 16
      : 0;
    main.scrollTo({ top });
  }, [slug, location.hash]);

  if (!chapter) return <Navigate to="/manual" replace />;

  const Icon = chapterIcon(chapter);
  const at = MANUAL_CHAPTERS.findIndex((entry) => entry.slug === chapter.slug);
  const previous = at > 0 ? MANUAL_CHAPTERS[at - 1] : undefined;
  const next = at >= 0 && at < MANUAL_CHAPTERS.length - 1 ? MANUAL_CHAPTERS[at + 1] : undefined;
  const allowed = !chapter.capability || capabilities.includes(chapter.capability);
  const note = accessNote(chapter, capabilities);

  return (
    <div className="space-y-4">
      <Link
        to="/manual"
        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500 transition-colors hover:text-slate-800"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
        사용 설명서
      </Link>

      <div className="flex gap-6">
        <article className="min-w-0 flex-1">
          <header className="rounded-xl border border-slate-200 bg-white px-5 py-5 sm:px-7 sm:py-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-th-3">
                <Icon className="h-6 w-6 text-th-6" strokeWidth={1.8} />
              </span>
              <div className="min-w-0 flex-1">
                <h1 className="text-[22px] font-semibold tracking-tight text-slate-900">{chapter.title}</h1>
                <p className="mt-1 text-[14px] leading-relaxed text-slate-500">{chapter.summary}</p>
                {note && (
                  <p className="mt-3 flex items-start gap-1.5 rounded-md bg-slate-100 px-2.5 py-1.5 text-[12px] leading-relaxed text-slate-600">
                    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                    {note}
                  </p>
                )}
              </div>
              {chapter.menuPath && allowed && (
                <Link to={chapter.menuPath} className="btn btn-primary shrink-0 self-start">
                  이 화면 열기
                  <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
                </Link>
              )}
            </div>
          </header>

          {chapter.sections.map((section) => (
            <section
              key={section.id}
              id={section.id}
              className="mt-4 scroll-mt-4 rounded-xl border border-slate-200 bg-white px-5 py-5 sm:px-7 sm:py-6"
            >
              <h2 className="text-[17px] font-semibold tracking-tight text-slate-900">{section.title}</h2>
              {section.blocks.map((block, blockAt) => (
                <Fragment key={blockAt}>
                  <Block block={block} />
                </Fragment>
              ))}
            </section>
          ))}

          {(previous || next) && (
            <nav className="mt-4 flex items-stretch gap-3">
              {previous ? (
                <Link
                  to={`/manual/${previous.slug}`}
                  className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 transition-colors hover:border-slate-300 hover:bg-slate-50"
                >
                  <p className="text-[11.5px] text-slate-400">이전</p>
                  <p className="mt-0.5 text-[13.5px] font-medium text-slate-800">{previous.title}</p>
                </Link>
              ) : (
                <div className="flex-1" />
              )}
              {next ? (
                <Link
                  to={`/manual/${next.slug}`}
                  className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-right transition-colors hover:border-slate-300 hover:bg-slate-50"
                >
                  <p className="text-[11.5px] text-slate-400">다음</p>
                  <p className="mt-0.5 text-[13.5px] font-medium text-slate-800">{next.title}</p>
                </Link>
              ) : (
                <div className="flex-1" />
              )}
            </nav>
          )}
        </article>

        {/* 목차는 넓은 화면에서만. 좁은 화면에서 본문 위에 얹으면 읽을 것을 가린다. */}
        {chapter.sections.length > 1 && (
          <aside className="hidden w-52 shrink-0 xl:block">
            <div className="sticky top-0">
              <p className="mb-2 px-3 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-slate-400">이 장의 내용</p>
              <ul className="space-y-0.5">
                {chapter.sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#/manual/${chapter.slug}#${section.id}`}
                      className="block rounded-md px-3 py-1.5 text-[12.5px] leading-snug text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
